import React, { useRef, useState } from 'react';
import { useAuth } from '@clerk/clerk-react';
import { colors } from '../theme';

const API_BASE = process.env.REACT_APP_API_URL;

// Stage photo attachments (issue #634): document how the unit arrived, damage
// found at intake, repair evidence etc. Photos are stored on the case
// (rma.caseImages, tagged with the stage) and rendered in the case history.
export default function RmaCaseImages({ rma, stage, title, helper, onSave }) {
  const { getToken } = useAuth();
  const inputRef = useRef(null);
  const [caption, setCaption] = useState('');
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');

  const images = (rma?.caseImages || []).filter(img => img.stage === stage);

  const handleFiles = async files => {
    if (!files || !files.length) return;
    setUploading(true);
    setError('');
    try {
      const token = await getToken();
      const formData = new FormData();
      Array.from(files).forEach(file => formData.append('images', file));
      formData.append('stage', stage);
      if (caption.trim()) formData.append('caption', caption.trim());
      const res = await fetch(`${API_BASE}/rma/${rma._id}/images`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok || !data.ok) {
        throw new Error(data.message || `Upload failed (${res.status})`);
      }
      setCaption('');
      if (onSave) onSave(data.rma, { keepOpen: true });
    } catch (err) {
      setError(err.message || 'Upload failed.');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  };

  return (
    <div style={{ border: `1px solid ${colors.panelBorder}`, borderRadius: '16px', padding: '1rem', marginTop: '0.75rem', backgroundColor: colors.surfaceRaised, textAlign: 'left' }}>
      <h3 style={{ margin: '0 0 0.35rem', fontSize: '1rem', color: colors.textStrong }}>{title || 'Photos'}</h3>
      <div style={{ fontSize: '0.8rem', color: colors.textDim, marginBottom: '0.5rem' }}>
        {helper || 'Attach photos (max 10 MB each). They are stored on the case and shown in the history.'}
      </div>
      {images.length > 0 && (
        <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem', marginBottom: '0.6rem' }}>
          {images.map((img, i) => (
            <a key={i} href={img.url} target="_blank" rel="noreferrer" title={img.caption || img.name}>
              <img
                src={img.url}
                alt={img.caption || img.name || 'case attachment'}
                style={{ width: 72, height: 72, objectFit: 'cover', borderRadius: 8, border: `1px solid ${colors.panelBorder}` }}
              />
            </a>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
        <input
          type="text"
          value={caption}
          onChange={e => setCaption(e.target.value)}
          placeholder="Caption (optional, applies to this upload)"
          style={{ flex: 1, minWidth: 180, background: colors.surface, border: `1px solid ${colors.panelBorder}`, color: colors.textBody, borderRadius: 8, padding: '0.45rem 0.6rem', fontSize: '0.85rem' }}
        />
        <button
          type="button"
          onClick={() => inputRef.current && inputRef.current.click()}
          disabled={uploading}
          style={{ background: 'rgba(70,187,137,0.12)', border: '1px solid rgba(70,187,137,0.35)', color: colors.primary, borderRadius: 8, padding: '0.45rem 0.9rem', fontWeight: 700, fontSize: '0.85rem', cursor: uploading ? 'wait' : 'pointer' }}
        >
          {uploading ? 'Uploading…' : '📷 Add photos'}
        </button>
        <input
          ref={inputRef}
          type="file"
          accept="image/jpeg,image/png,image/webp,image/gif,application/pdf"
          multiple
          style={{ display: 'none' }}
          onChange={e => handleFiles(e.target.files)}
        />
      </div>
      {error && <div style={{ marginTop: 6, color: colors.danger, fontSize: '0.8rem' }}>{error}</div>}
    </div>
  );
}
